import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Loader2, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const statusMeta = {
  new: { label: "未対応", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  read: { label: "確認済", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  replied: { label: "対応済", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};

export function ApplicationsPanel({ query = "", onChanged }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () =>
    api.get("/applications").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !items) return items || [];
    return items.filter((a) =>
      [a.name, a.email, a.position, a.message, a.phone].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [items, query]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success("ステータスを更新しました");
      load();
      onChanged?.();
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      toast.success("削除しました");
      setSelected(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <>
      {items === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-white/40" data-testid="applications-empty">
          {items.length === 0 ? "まだ応募はありません。" : "条件に一致する応募はありません。"}
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="applications-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="text-left text-xs text-white/40 border-b border-white/10">
                <th className="px-5 py-3.5 font-medium">日時</th>
                <th className="px-2 py-3.5 font-medium">氏名</th>
                <th className="px-2 py-3.5 font-medium">応募職種</th>
                <th className="px-2 py-3.5 font-medium">メール</th>
                <th className="px-2 py-3.5 font-medium">ステータス</th>
                <th className="px-5 py-3.5 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  data-testid={`application-row-${a.id}`}
                  onClick={() => setSelected(a)}
                  className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3.5 text-white/50 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString("ja-JP", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-2 py-3.5 font-jp font-bold text-white">{a.name}</td>
                  <td className="px-2 py-3.5 text-gold/90">{a.position}</td>
                  <td className="px-2 py-3.5 text-white/60">{a.email}</td>
                  <td className="px-2 py-3.5">
                    <Badge variant="outline" className={statusMeta[a.status]?.cls}>{statusMeta[a.status]?.label}</Badge>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={(e) => { e.stopPropagation(); setSelected(a); }} data-testid={`application-view-${a.id}`} className="text-white/40 hover:text-gold transition-colors"><Eye size={16} /></button>
                      <button onClick={(e) => { e.stopPropagation(); remove(a.id); }} data-testid={`application-delete-${a.id}`} className="text-white/40 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelected(null)} data-testid="application-detail-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-gold mb-2">{selected.position}</p>
                <h3 className="font-jp text-2xl font-bold">{selected.name}</h3>
              </div>
              <Badge variant="outline" className={statusMeta[selected.status]?.cls}>{statusMeta[selected.status]?.label}</Badge>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">メール</dt><dd className="text-white/90 break-all">{selected.email}</dd></div>
              {selected.phone && <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">電話番号</dt><dd className="text-white/90">{selected.phone}</dd></div>}
              <div className="flex gap-4"><dt className="w-24 text-white/40 shrink-0">受信</dt><dd className="text-white/90">{new Date(selected.created_at).toLocaleString("ja-JP")}</dd></div>
              <div className="pt-2"><dt className="text-white/40 mb-2">メッセージ</dt><dd className="text-white/90 leading-relaxed whitespace-pre-wrap bg-white/[0.03] rounded-lg p-4">{selected.message}</dd></div>
            </dl>
            <div className="mt-7 flex items-center gap-3">
              <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                <SelectTrigger className="flex-1 bg-white/[0.03] border-white/10" data-testid="application-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">未対応</SelectItem>
                  <SelectItem value="read">確認済</SelectItem>
                  <SelectItem value="replied">対応済</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={() => setSelected(null)} className="rounded-lg border border-white/10 px-5 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="application-close-modal">閉じる</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
